import React, { useEffect, useRef, useState } from 'react';
import {
  Clipboard,
  Image,
  Pressable,
  ScrollView,
  Share,
  TextInput,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from '../../components/ui/Button';
import Icon, { IconName } from '../../components/ui/Icon';
import Text from '../../components/ui/Text';
import useReferrals from '../../hooks/useReferrals';
import { statusLabel } from '../../services/referrals';
import {
  FRIEND_REWARD,
  REFERRER_REWARD,
  referralMessage,
  referralSteps,
} from '../../data/referral';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  shadows,
  spacing,
  useResponsive,
} from '../../theme';

/** How long the tile reads "Copied" before going back to "Copy". */
const COPIED_MS = 2000;

/**
 * The illustration's own ground, sampled from its top edge.
 *
 * A literal rather than a theme token because it belongs to the ARTWORK, not
 * to the palette: the header strip above the banner is painted with it so the
 * two meet seamlessly, and if the illustration is ever replaced this has to
 * be re-sampled from the new one. `colors.background` is a cooler green and
 * left a visible line where the two met.
 */
const BANNER_GROUND = '#FEFAF0';

/** The illustration at the top of the page. */
const REFER_ART = require('../../../assets/uploads/banner/earn.png');

/**
 * The banner's aspect - the artwork's own, so the image fills the frame with
 * nothing cropped and no ground showing behind it.
 *
 * The source is 1536x1024, which is a 1.5:1 block - 260pt tall on a phone,
 * a third of the screen for a decorative header. It is cropped to 2.1:1 on
 * disk instead, centred on the two figures so both are whole; the space that
 * went was empty sky above them and empty floor below.
 *
 * This artwork carries NO TEXT, unlike the one it replaced - that one had
 * "Refer & Earn" painted into it, which is why the page needed no heading of
 * its own. The heading below now does that work.
 */
const BANNER_ASPECT = 1536 / 731;

/**
 * Dots in the run between two steps.
 *
 * An UPPER BOUND, not a measurement: the run is clipped to its box, so extra
 * dots cost nothing while a shortfall leaves a visible gap before the last
 * mark. At a 6pt pitch (a 3pt dot plus a 3pt gap) 90 dots cover 540pt - past
 * the span even on a wide tablet, where the row is at its longest.
 *
 * Sized generously on purpose. The alternative is measuring the row with
 * `onLayout` and computing a count, which is a re-render and a state update
 * to draw a decoration; clipping a fixed run costs neither.
 */
const LINK_DOTS = Array.from({ length: 90 }, (_, i) => i);

/**
 * Whether a customer may enter somebody else's code from this screen.
 *
 * ---------------------------------------------------------------------------
 * OFF, AND NOT BECAUSE IT DOES NOT WORK
 * ---------------------------------------------------------------------------
 * The card below it is complete and tested; so is everything behind it. This
 * is a product question that has not been answered yet, and the two answers
 * are both defensible:
 *
 *   SIGNUP ONLY - a referral means somebody joined because they were told to.
 *   Entering a code after the fact breaks that story, and lets a customer who
 *   arrived on their own go and find a code before their first order, paying
 *   a "referrer" who did nothing.
 *
 *   UNTIL THE FIRST JOB - which is what the server already allows, and always
 *   has: `attribute()` refuses on a COMPLETED BOOKING, not on an old account.
 *   Today a genuine referral dies over a typo, a dropped request, or a
 *   collapsed link nobody noticed, and the referrer is never paid for a
 *   referral they really made.
 *
 * Turned on by flipping this to `true` - the entry-card test reads this same
 * constant, so it asserts whichever way it is set and nothing has to be
 * remembered. Nothing else changes: the server needs no change either way.
 *
 * What is NOT gated by this: the alert on a failed code at sign-up, and the
 * "code applied" card below. Those are honest either way - silence about a
 * code that did not attach was wrong under both answers.
 */
export const CODE_ENTRY_ENABLED = false;

/**
 * Refer & Earn.
 *
 * The page is arranged around the one thing it exists to make happen: getting
 * the code out of the app and into a message. So the code and the share
 * button are the first screenful, the explanation is under them, and the
 * ledger of who has joined is under that - the reverse of how the data is
 * shaped, and the right order for what somebody came here to do.
 *
 * ---------------------------------------------------------------------------
 * THE PROGRAMME IS NOT LIVE
 * ---------------------------------------------------------------------------
 * See `data/referral.ts`. The code is derived from the phone number rather
 * than issued by a server, nothing watches for signups, and no reward has ever
 * been paid. What this screen must therefore never do is show an earnings
 * figure it cannot back - so the total reads zero for a new account and the
 * list is honestly empty, rather than seeded with names of friends who do not
 * exist.
 *
 * Signed out there is no account to key a code on, so the page shows the
 * terms and asks for a sign-in instead of rendering a code nobody can redeem.
 */

type Props = {
  onBack: () => void;
  /**
   * The verified number, digits only. Null when signed out.
   *
   * No longer used to build the code - that comes from the server now - but
   * kept on the props so callers do not have to change.
   */
  phone?: string | null;
  signedIn: boolean;
  /** Opens the sign-in sheet, for the signed-out state. */
  onSignIn: () => void;
};

function ReferralScreen({ onBack, signedIn, onSignIn }: Props) {
  const resp = useResponsive();
  /* The status bar's height. This page renders inside `HomeScreen`'s absolute
     overlay, which is pinned above the safe area - see `topBar`. */
  const insets = useSafeAreaInsets();

  /*
   * The code, the reward policy and the referral list, from the server.
   *
   * `referralFor(phone)` derived a code from the phone number locally. That
   * could never have worked against the real backend: codes live in
   * `ReferralCode`, are unique per customer, and ops can BLOCK one. A derived
   * code would be shared and then refused, with the referrer never told why.
   *
   * ---------------------------------------------------------------------
   * WHAT THE SERVER DOES NOT SEND, AND WHY THE ROWS LOOK DIFFERENT
   * ---------------------------------------------------------------------
   * `GET /referrals/sent` returns raw `Referral` rows - no referee NAME, by
   * design: who accepted your code is somebody else's personal data. The mock
   * had `name` and `when` per row and rendered an initial in a circle. Names
   * are gone; the row is keyed off status and date instead. Inventing a name
   * client-side, or showing the friend's phone, are both worse answers.
   */
  const referrals = useReferrals(signedIn);
  const remote = referrals.summary.data;
  const rows = referrals.sent.data ?? [];
  /**
   * The referral this customer is on the receiving end of, if any.
   *
   * `null` from a settled request means they signed up without a code and the
   * entry form belongs on screen; `null` while `received.loading` means the
   * answer has not arrived, and drawing the form then would flash an invite
   * to somebody who already used one.
   */
  const mine = referrals.received.data;

  /** What is currently typed into the entry field. */
  const [entry, setEntry] = useState('');

  /**
   * Apply what was typed.
   *
   * The hook owns the failure - it prefers the server's sentence and only
   * writes its own when a status arrived with no body - so there is nothing
   * to report here. On success it reloads `received`, which swaps this whole
   * card for the applied one; clearing the field keeps a code that worked
   * from sitting in a box that is about to disappear.
   */
  const submitCode = async () => {
    if (await referrals.apply(entry)) setEntry('');
  };

  /*
   * The banner runs to the true screen edge, out of the page's own column.
   *
   * `resp.column` pads this ScrollView's content and, on a tablet, centres
   * it - so a child at `width: '100%'` stops short of both edges. The pixel
   * width plus a matching negative margin pulls it back out, whatever that
   * gutter measures.
   */
  const bannerWidth = resp.width;
  const bleed = -(screenPadding + resp.sideInset);

  /*
   * "Copied" lives on the control that was pressed, not in a toast.
   *
   * Copying is otherwise completely silent - nothing moves, no sheet opens -
   * so without this the tap has no visible result at all and people press it
   * again. A banner at the top of the screen would make someone look away
   * from the thing they just touched to find out whether it worked.
   */
  const [copied, setCopied] = useState(false);

  /* Cleared on unmount so a pending reset cannot fire into a dead component. */
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copiedTimer.current) {
        clearTimeout(copiedTimer.current);
      }
    },
    [],
  );

  /**
   * Put the code on the clipboard.
   *
   * `Clipboard` from react-native core is deprecated in favour of
   * `@react-native-clipboard/clipboard`, and it still works: the native module
   * ships in this version (`ClipboardModule.kt` on Android, RCTClipboard on
   * iOS) and is registered in RN's own core module list. Using it avoids
   * adding a native dependency - and a rebuild for everyone on the team - for
   * one twelve-character string.
   *
   * When it is finally removed, install that package and change this one line;
   * the API is `setString` either way.
   */
  const copyCode = () => {
    Clipboard.setString(remote?.code ?? '');
    setCopied(true);
    if (copiedTimer.current) {
      clearTimeout(copiedTimer.current);
    }
    /* Back to "Copy" after a moment. A label stuck on "Copied" stops being
       feedback and becomes the button's name. */
    copiedTimer.current = setTimeout(() => setCopied(false), COPIED_MS);
  };

  const share = () => {
    Share.share({
      // The backend writes the share text, code already embedded.
      message: remote?.shareMessage ?? referralMessage(remote?.code ?? ''),
    }).catch(() => {
      /* Dismissing the share sheet rejects on iOS. That is not a failure and
         there is nothing to tell anybody about. */
    });
  };

  return (
    <View style={styles.root}>
      {/*
        The way back, on the page rather than on the picture.

        It used to float in the artwork's top-left corner, which is exactly
        where the wordmark is drawn - the disc sat on top of "Refer" and read
        as a defect. The banner is an inset card now, so there is page above
        it for the arrow to live on without covering anything.
      */}
      <View style={[styles.topBar, { paddingTop: spacing.sm + insets.top }]}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back"
          onPress={onBack}
          hitSlop={12}
          style={({ pressed }) => [
            styles.backButton,
            pressed ? styles.pressed : null,
          ]}
        >
          <Icon name="arrowLeft" size={24} color={colors.ink} strokeWidth={2} />
        </Pressable>

        {/*
          The page's title, which the artwork used to carry.

          The old banner had "Refer & Earn" painted into it, so the page could
          go without a heading. This one is illustration only - without a
          title here the page would open on two men and a dotted arrow and
          never say what it is.
        */}
        <Text variant="title" align="center" style={styles.topTitle}>
          Refer & Earn
        </Text>

        {/* An empty flank the width of the arrow, so the title centres on the
            strip rather than on the space left over beside it. */}
        <View style={styles.topSpacer} />
      </View>

      <ScrollView
        style={styles.scroller}
        contentContainerStyle={[styles.page, resp.column]}
        showsVerticalScrollIndicator={false}
      >
        {/* ----------------------------------------------------- the banner */}
        {/*
          A card in the page's gutter, under the back arrow's own strip.

          `PageHeader` used to sit above it and cut the page in two: a white
          bar with a title, then a picture saying the same thing in paint. The
          title is redundant once the banner is the first thing you see, so
          only the arrow survives.

          The artwork carries the offer, so the copy under it does not repeat
          what the picture already says.
        */}
        <View
          style={[styles.banner, { width: bannerWidth, marginLeft: bleed }]}
        >
          <Image
            source={REFER_ART}
            style={styles.bannerArt}
            /*
              `cover`, and the frame is the artwork's own aspect - so there is
              nothing to crop. Cover rather than contain so that a re-exported
              asset a hair off the ratio still fills the band, instead of
              letterboxing itself and reintroducing the ground this was meant
              to remove.
            */
            resizeMode="cover"
            /* Decoded to the size drawn and no Android cross-fade - the same
               treatment every other bundled photograph in the app gets. */
            resizeMethod="resize"
            fadeDuration={0}
          />
        </View>

        <View style={[styles.offer, styles.inset]}>
          <View style={styles.offerMark}>
            <Icon name="gift" size={22} color={colors.primary} />
          </View>
          <View style={styles.offerBody}>
            {/* The reward policy is the server's, not a constant compiled in:
                ops change these without an app release, and a stale figure
                here is a promise the wallet would not honour. */}
            <Text variant="subtitle" color={colors.primary}>
              You earn {remote?.referrerCoins ?? REFERRER_REWARD} coins
            </Text>
            <Text variant="caption" color={colors.inkMuted}>
              {`when your friend completes their first service - and they get ${
                remote?.refereeCoins ?? FRIEND_REWARD
              } coins${
                remote
                  ? `, if they book within ${remote.qualifyWindowDays} days`
                  : ''
              }`}
            </Text>
          </View>
        </View>

        {/* --------------------------------------- a code somebody gave me */}
        {/*
          The other half of the programme, and the half that had no screen.

          A code could only ever be typed into the sign-in sheet, at the one
          moment a new customer is least interested in it - and if it was
          wrong, blocked, or already used, the sheet swallowed the failure
          without a word. Anyone who missed it there had no second chance
          anywhere in the app, though the server has always allowed one right
          up until their first job completes.

          Three states, in the order they happen: nothing entered yet, an
          entered code waiting on that first booking, and the reward paid.
          Hidden entirely while signed out - there is no account to attach a
          code to, and the sign-in call to action already sits below.
        */}
        {signedIn && !referrals.received.loading ? (
          mine ? (
            <View style={[styles.offer, styles.inset]}>
              <View style={styles.offerMark}>
                <Icon
                  name={mine.status === 'rewarded' ? 'wallet' : 'gift'}
                  size={22}
                  color={colors.primary}
                />
              </View>
              <View style={styles.offerBody}>
                <Text variant="subtitle" color={colors.primary}>
                  Code {mine.code} applied
                </Text>
                <Text variant="caption" color={colors.inkMuted}>
                  {mine.status === 'rewarded'
                    ? `${mine.refereeCoins} coins credited to your wallet`
                    : mine.status === 'expired'
                    ? 'This one ran out of time before a first booking'
                    : `${mine.refereeCoins} coins land once your first booking is complete`}
                </Text>
              </View>
            </View>
          ) : CODE_ENTRY_ENABLED ? (
            <View style={[styles.codeCard, styles.inset]}>
              <Text variant="labelSm" color={colors.inkMuted}>
                HAVE A FRIEND'S CODE?
              </Text>

              <View style={styles.entryRow}>
                <TextInput
                  value={entry}
                  onChangeText={next => {
                    setEntry(next.toUpperCase());
                    /* The last failure belongs to the last attempt. Left up
                       while somebody retypes, it reads as a verdict on what
                       they are typing now. */
                    if (referrals.applyError) referrals.clearApplyError();
                  }}
                  placeholder="ENTER CODE"
                  placeholderTextColor={colors.inkSubtle}
                  autoCapitalize="characters"
                  autoCorrect={false}
                  maxLength={12}
                  editable={!referrals.applying}
                  style={styles.entryInput}
                  accessibilityLabel="Friend's referral code"
                  onSubmitEditing={submitCode}
                />
              </View>

              {/* The server's own sentence - it knows whether the code was
                  unknown, blocked, self-referred or already used, and says so
                  more precisely than this screen could guess. */}
              {referrals.applyError ? (
                <Text variant="caption" color={colors.danger}>
                  {referrals.applyError}
                </Text>
              ) : null}

              <Button
                label={referrals.applying ? 'Applying…' : 'Apply code'}
                onPress={submitCode}
                disabled={!entry.trim() || referrals.applying}
              />

              <Text variant="caption" color={colors.inkMuted}>
                {`You will get ${
                  remote?.refereeCoins ?? FRIEND_REWARD
                } coins once your first booking is complete. Only before your first job - after that, the code no longer applies.`}
              </Text>
            </View>
          ) : null
        ) : null}

        {/* ------------------------------------------------------- the code */}
        {signedIn && remote?.code ? (
          <View style={[styles.codeCard, styles.inset]}>
            <Text variant="labelSm" color={colors.inkMuted}>
              YOUR CODE
            </Text>

            <Pressable
              accessibilityRole="button"
              /* Spaced out for the screen reader, which would otherwise say
                 the code as a single unpronounceable word. */
              accessibilityLabel={`Copy your code, ${remote.code
                .split('')
                .join(' ')}`}
              onPress={copyCode}
              style={({ pressed }) => [
                styles.codeBox,
                pressed ? styles.pressed : null,
              ]}
            >
              {/* Letter-spaced, because this is a string somebody may have to
                  read out loud or type by hand. */}
              <Text variant="titleLg" style={styles.code}>
                {remote.code}
              </Text>

              {/*
                Copy, not share - sharing is what the button below does, and
                two controls for the same thing on one card is one too many.

                The label flips to "Copied" rather than the icon changing
                alone: a tick appearing where a glyph was is easy to miss, and
                copying is silent otherwise - nothing on screen would
                otherwise confirm it happened.
              */}
              <View style={styles.copyTag}>
                <Icon
                  name={copied ? 'check' : 'document'}
                  size={15}
                  color={colors.primary}
                />
                <Text variant="labelSm" color={colors.primary}>
                  {copied ? 'Copied' : 'Copy'}
                </Text>
              </View>
            </Pressable>

            <Button label="Share your code" onPress={share} />
          </View>
        ) : (
          /*
           * Signed out.
           *
           * The offer above still renders - it is the reason to sign in - but
           * there is no code, because a code that cannot be tied to an account
           * cannot pay anybody. Asking for the sign-in here rather than
           * showing a disabled button says which of the two is missing.
           */
          <View style={[styles.codeCard, styles.inset]}>
            <Text variant="subtitle" align="center">
              Sign in to get your code
            </Text>
            <Text variant="caption" align="center" color={colors.inkMuted}>
              Your referral code is tied to your account, so we know where to
              send the reward.
            </Text>
            <Button label="Sign in" onPress={onSignIn} />
          </View>
        )}

        {/* --------------------------------------------------- how it works */}
        {/*
          Three abreast on the page itself, not stacked inside a card.

          These are one sequence, and read across they show it in a glance -
          share, they sign up, you earn. Stacked in a card they were three
          paragraphs the eye has to work down, and the card drew a box around
          an explanation that is not a thing you can act on.
        */}
        <View style={[styles.bare, styles.inset]}>
          <Text variant="subtitle">How it works</Text>

          <View style={styles.steps}>
            {/*
              One dotted run behind all three columns, not one per column.

              Drawn as a sibling of the steps rather than a child of one,
              because a child cannot reach into the next column: it used to be
              positioned `left: '50%'` with `right: '-50%'`, and a negative
              percentage does not stretch a box past its parent in React
              Native - so the run was clipped to the column that owned it and
              never crossed the gap it was supposed to bridge.

              Inset by half a column at each end so it starts and stops under
              the outer marks instead of running to the row's edges. The marks
              are opaque and paint over it, which is what makes the dots
              appear to run BETWEEN them.

              Drawn dots rather than `borderStyle: 'dashed'`, which rasterises
              to a solid hairline on Android at this weight.
            */}
            <View style={styles.stepLink} pointerEvents="none">
              {LINK_DOTS.map(dot => (
                <View key={dot} style={styles.stepLinkDot} />
              ))}
            </View>

            {referralSteps.map((step, i) => (
              <View key={step.title} style={styles.step}>
                <View style={styles.stepMark}>
                  <Icon
                    name={step.icon as IconName}
                    size={17}
                    color={colors.primary}
                    strokeWidth={1.7}
                  />
                  {/* The order, on the glyph rather than beside it - these
                      happen in sequence and the second cannot precede the
                      first, which a row of icons alone would not say. */}
                  <View style={styles.stepNumber}>
                    {/*
                      Sized here rather than by a variant. `caption` carries an
                      18pt line height, which is taller than the badge holding
                      it - the digit sat proud of its own circle. This is the
                      one place in the app that needs type smaller than the
                      ramp goes.
                    */}
                    <Text
                      variant="caption"
                      align="center"
                      color={colors.onPrimary}
                      style={styles.stepNumberText}
                    >
                      {i + 1}
                    </Text>
                  </View>
                </View>
                {/* The title only. `step.detail` is deliberately not drawn -
                    three columns of small print under three icons was more
                    reading than a glance-level sequence is worth, and the
                    titles already say share / they book / you earn. */}
                <Text variant="labelSm" align="center">
                  {step.title}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* ------------------------------------------------------ the tally */}
        {/*
          Two figures, side by side: what the referrals have paid out and how
          many people took the code. They sit above the list because they are
          the answer to "is this working" - the list is the detail behind them.

          Only when signed out is this hidden, because both numbers would be
          zero for want of an account rather than for want of referrals.
        */}
        {signedIn ? (
          <View style={[styles.tally, styles.inset]}>
            <View style={styles.tallyCell}>
              <View style={styles.tallyMark}>
                <Icon name="wallet" size={18} color={colors.primary} />
              </View>
              <View style={styles.tallyBody}>
                <Text variant="caption" color={colors.inkMuted}>
                  Total earnings
                </Text>
                <Text variant="subtitle">
                  {remote?.totalCoinsEarned ?? 0} coins
                </Text>
              </View>
            </View>

            <View style={styles.tallyDivider} />

            <View style={styles.tallyCell}>
              <View style={styles.tallyMark}>
                <Icon name="users" size={18} color={colors.primary} />
              </View>
              <View style={styles.tallyBody}>
                <Text variant="caption" color={colors.inkMuted}>
                  Friends referred
                </Text>
                <Text variant="subtitle">
                  {remote?.totalReferrals ?? rows.length}
                </Text>
              </View>
            </View>
          </View>
        ) : null}

        {/* ----------------------------------------------------- the ledger */}
        {/*
          The heading sits on the page and only the rows are carded.

          A title inside the card labelled the card; outside it labels the
          section, which is what lets the list below read as the content under
          a heading rather than as a box with a caption at the top.
        */}
        <View style={[styles.ledgerHead, styles.inset]}>
          <Text variant="subtitle">Recent referrals</Text>
        </View>

        <View style={[styles.section, styles.inset]}>
          {rows.length === 0 ? (
            /*
             * An empty state that does not apologise.
             *
             * Nobody has used the code because it has not been shared yet -
             * that is the normal first visit, not a failure, so the copy says
             * what happens next rather than "no data".
             */
            <View style={styles.empty}>
              <Icon name="users" size={22} color={colors.borderStrong} />
              <Text variant="bodySm" align="center" color={colors.inkMuted}>
                Nobody has used your code yet
              </Text>
              <Text variant="caption" align="center" color={colors.inkSubtle}>
                Friends who join with it appear here, along with what you have
                earned.
              </Text>
            </View>
          ) : (
            rows.map((referral, i) => (
              <View key={referral.id}>
                {i > 0 ? <View style={styles.separator} /> : null}
                <View style={styles.referral}>
                  {/* The mark carried the friend's initial. The server does
                      not send their name - deliberately, it is their data -
                      so it carries the status instead. */}
                  <View style={styles.referralMark}>
                    <Text variant="labelSm" color={colors.primary}>
                      {referral.status === 'rewarded' ? '✓' : '·'}
                    </Text>
                  </View>
                  <View style={styles.referralBody}>
                    <Text variant="bodySm">{statusLabel(referral.status)}</Text>
                    <Text variant="caption" color={colors.inkMuted}>
                      {new Date(referral.createdAt).toLocaleDateString()}
                    </Text>
                  </View>
                  {/* A figure only once it is real money. Anything earlier
                      would be a projection dressed up as a credit. */}
                  <Text
                    variant="bodySm"
                    color={
                      referral.status === 'rewarded'
                        ? colors.success
                        : colors.inkSubtle
                    }
                  >
                    {referral.status === 'rewarded'
                      ? `+${referral.referrerCoins} coins`
                      : 'Pending'}
                  </Text>
                </View>
              </View>
            ))
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroller: {
    flex: 1,
  },
  /*
   * No horizontal padding here.
   *
   * `resp.column` is applied alongside this on the same contentContainer and
   * already carries the gutter. Setting `padding` as well applies it twice.
   *
   * Each block below owns its own side inset instead, via `inset`.
   */
  page: {
    gap: spacing.md,
    /*
     * Just enough to clear the floating tab bar.
     *
     * This was 120, which is far more than the bar is tall - so the page
     * ended in a screenful of empty background below the last line of terms.
     * The bar is ~64pt over the safe-area inset; 84 clears it with a margin
     * and nothing more.
     */
    paddingBottom: 84,
  },
  /* The page gutter, shared by every block including the banner. */
  inset: {
    marginHorizontal: screenPadding,
  },

  /*
   * The banner, running to both screen edges.
   *
   * No corner radius and no gutter: it is the top of the page rather than a
   * card on it, and it meets the header strip above with no seam because both
   * carry `BANNER_GROUND`.
   *
   * This was safe to do only once the artwork changed. The previous
   * illustration had "Refer & Earn" painted into its own left edge, so
   * full-bleed pushed the wordmark under the back arrow and off the screen.
   * This one is illustration only, centred, with clear margins either side -
   * there is nothing at the edges to lose.
   *
   * `aspectRatio` is the artwork's own, so the image fills the frame with
   * nothing cropped. `overflow: 'hidden'` is the guard if the asset is ever
   * replaced with a different ratio - `cover` crops it here rather than
   * spilling over the blocks below.
   */
  banner: {
    aspectRatio: BANNER_ASPECT,
    overflow: 'hidden',
    backgroundColor: BANNER_GROUND,
  },
  /*
   * The strip the back arrow sits on, above the banner.
   *
   * Its own row rather than a floating disc on the artwork: the picture has
   * the wordmark in that corner, and anything laid over it covers the word.
   *
   * ---------------------------------------------------------------------------
   * IT CARRIES THE BANNER'S COLOUR, AND THE RULE UNDER IT IS GONE
   * ---------------------------------------------------------------------------
   * This has been changed twice, so the reasoning is recorded rather than the
   * conclusion alone.
   *
   * `BANNER_GROUND` is sampled from the top edge of the illustration directly
   * below, so the strip and the artwork meet with no seam and the banner reads
   * as continuing up behind the arrow. It was briefly `colors.surface` on the
   * argument that a header is chrome and chrome should be one colour across
   * the account - but on this page the white strip against the cream banner
   * read as two mismatched panels butted together, which is the more visible
   * problem of the two.
   *
   * The bottom rule goes with it: a hairline between two identical colours is
   * a line drawn across the middle of one surface, which is exactly the seam
   * the shared colour is there to remove.
   */
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    /* `paddingTop` is applied in the render, where the status bar's height can
       be added to it - this page is mounted in `HomeScreen`'s absolute overlay,
       which sits above the safe area, so a bare `spacing.sm` put the arrow and
       the title underneath the clock. Same fix as `PageHeader`'s. */
    paddingBottom: spacing.sm,
    paddingHorizontal: screenPadding,
    backgroundColor: BANNER_GROUND,
  },
  /* Centred on the strip, not on the space beside the arrow - the flanks
     below are what make that true. */
  topTitle: {
    flex: 1,
  },
  /* The arrow's own width, mirrored on the right. Equal flanks are what
     centre the title: each claims the same space whatever is inside it. */
  topSpacer: {
    width: 38,
  },
  /*
   * Nudged in from the gutter rather than hard against it.
   *
   * `marginLeft` on top of the strip's own padding: at the bare gutter the
   * glyph's ink sits closer to the screen edge than any other control on the
   * page, because the arrow is drawn to the left of its own box.
   */
  backButton: {
    marginLeft: spacing.xs,
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bannerArt: {
    width: '100%',
    height: '100%',
  },
  /* The two figures, as one card split down the middle. */
  tally: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  tallyCell: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  tallyMark: {
    width: 34,
    height: 34,
    borderRadius: radii.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  /* Zero basis, so a four-digit total does not push its neighbour off. */
  tallyBody: {
    flex: 1,
    minWidth: 0,
    gap: 1,
  },
  tallyDivider: {
    width: hairline.borderWidth,
    alignSelf: 'stretch',
    marginHorizontal: spacing.sm,
    backgroundColor: hairline.borderColor,
  },
  /* The offer, as a strip under the picture rather than words on top of it -
     text over artwork is legible until the artwork changes. */
  offer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surfaceAccent,
  },
  offerMark: {
    width: 40,
    height: 40,
    borderRadius: radii.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
  },
  offerBody: {
    flex: 1,
    gap: 2,
  },

  codeCard: {
    gap: spacing.md,
    padding: spacing.xl,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  /* Dashed, so it reads as something to be torn off and handed over rather
     than as another input field. */
  codeBox: {
    flexDirection: 'row',
    alignItems: 'center',
    /*
     * Spread, not centred.
     *
     * Centred, the whole row re-centres when the label flips between "Copy"
     * and "Copied" - so the code itself visibly jumps left on every tap, at
     * the exact moment someone is checking it copied correctly. Pinning the
     * code to one side and the control to the other keeps it still.
     */
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: radii.md,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.primary,
    backgroundColor: colors.surfaceAccent,
  },
  code: {
    letterSpacing: 2,
  },
  /* A labelled control rather than a bare glyph: "Copy" says what the tap
     does, which an icon on a dashed box does not. */
  copyTag: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing.xs,
    /* Wide enough for "Copied", so the glyph does not slide left when the
       longer word replaces the shorter one. */
    minWidth: 68,
  },

  /* Solid-bordered, deliberately unlike `codeBox` above it.
     That one is dashed because it is a coupon to hand over; this one is a
     field to type into, and the two sitting on one screen must not look
     like the same control doing two different things. */
  entryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    borderRadius: radii.md,
    ...hairline,
    backgroundColor: colors.surfaceMuted,
  },
  entryInput: {
    flex: 1,
    paddingVertical: spacing.md,
    /* Matches `code` above: a referral code is read character by character,
       and tracking is what stops O and 0 running together. */
    letterSpacing: 2,
    color: colors.ink,
  },

  /* A block that belongs to the page rather than to a card. */
  bare: {
    gap: spacing.md,
  },
  /*
   * The three steps, abreast and flush with the page.
   *
   * No `paddingLeft`: it used to nudge the row in so the first glyph cleared
   * the gutter, but the columns are centred within themselves - the padding
   * shifted all three right and left the block visibly out of line with the
   * heading above it and the cards below.
   */
  steps: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  /*
   * The dotted run between one mark and the next.
   *
   * Pinned at the marks' own vertical centre and clipped to the column, so it
   * reads as a line joining them rather than as a rule under the heading.
   */
  /*
   * The dotted run, behind the three marks.
   *
   * `top: 18` puts it on the marks' own centre line: the mark is the first
   * child of a column, it is 38 tall, so its middle is 19 - less half the
   * dot's own height.
   *
   * Inset by a sixth of the row at each end. Three equal columns means each
   * centre sits a sixth of the way in from its own side, so this starts and
   * ends underneath the outer marks rather than at the row's edges.
   *
   * Overflow is clipped because `LINK_DOTS` is an upper bound - more dots
   * than the widest span needs, with the tail cut off rather than measured.
   */
  stepLink: {
    position: 'absolute',
    top: 18,
    left: '16.6%',
    right: '16.6%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    overflow: 'hidden',
  },
  /*
   * 3pt of dot, 3pt of gap.
   *
   * Was 2x1 in `colors.border`, which is a hairline in the palette's faintest
   * grey - at that size the run was there but effectively invisible against
   * the page. 2pt tall in the stronger border tone reads as a dotted line at
   * arm's length, which is the whole point of drawing one.
   */
  stepLinkDot: {
    width: 3,
    height: 2,
    borderRadius: 1,
    backgroundColor: colors.borderStrong,
  },
  /* Equal thirds - `minWidth: 0` so a long step title wraps inside its column
     instead of forcing the row wider than the screen. */
  step: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    gap: spacing.xs,
  },
  stepMark: {
    width: 38,
    height: 38,
    borderRadius: radii.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
    /* Over the dotted run, not under it - the line passes behind every mark
       and is hidden by it, which is what makes the dots read as joining the
       steps rather than striking through them. Android needs `elevation`;
       `zIndex` alone does not raise a sibling there. */
    zIndex: 1,
    elevation: 1,
  },
  /* On the mark's lower-right, so the glyph stays the thing you see first. */
  stepNumber: {
    position: 'absolute',
    right: -1,
    bottom: -1,
    /* A true circle: one to nine only, so it never has to grow for a second
       digit and `minWidth` would only let it drift out of round. */
    width: 14,
    height: 14,
    borderRadius: 7,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  /*
   * The digit itself.
   *
   * `lineHeight` has to come down with `fontSize` - left at the caption's
   * 18pt it centres the glyph against a box taller than the badge, which
   * pushes the number visibly low in its circle.
   */
  stepNumberText: {
    fontSize: 9,
    lineHeight: 11,
    /*
     * Stretched across the badge, then centred within itself.
     *
     * `alignItems: 'center'` on the badge centres the Text BOX, but the box
     * shrink-wraps the glyph - and a digit carries uneven side bearings, so
     * the ink inside it sat left of the box's own middle. Filling the width
     * and setting `textAlign` centres the glyph against the circle instead of
     * against its own advance width.
     */
    width: '100%',
    /* The digit's ink sits high in its line box; a hair of top padding drops
       it onto the badge's true centre line. */
    marginTop: 0.5,
  },
  section: {
    gap: spacing.md,
    padding: spacing.xl,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },

  ledgerHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  empty: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.lg,
  },
  referral: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.sm,
  },
  referralMark: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  referralBody: {
    flex: 1,
    gap: 2,
  },
  separator: {
    height: 1,
    backgroundColor: colors.border,
  },

  pressed: {
    opacity: 0.6,
  },
});

export default ReferralScreen;
